import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiPower,
  FiMapPin,
  FiClock,
  FiDollarSign,
  FiStar,
  FiTruck,
  FiRefreshCw,
  FiChevronRight,
  FiNavigation,
  FiX,
} from 'react-icons/fi';
import { Card, LoaderComponent, EmptyState } from '../../components/ui';
import driverService, { DriverStats, AvailableDelivery } from '../../services/driver.service';
import DriverRegister from './DriverRegister';

const deliveryStatusConfig: Record<string, { label: string; color: string }> = {
  AGUARDANDO: { label: 'Pendente', color: 'text-yellow-600 bg-yellow-100' },
  ATRIBUIDO: { label: 'Aceite', color: 'text-blue-600 bg-blue-100' },
  RECOLHIDO: { label: 'Recolhido', color: 'text-orange-600 bg-orange-100' },
  EM_TRANSITO: { label: 'Em Transito', color: 'text-purple-600 bg-purple-100' },
  ENTREGUE: { label: 'Entregue', color: 'text-green-600 bg-green-100' },
};

const DriverDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [online, setOnline] = useState(false);
  const [stats, setStats] = useState<DriverStats | null>(null);
  const [available, setAvailable] = useState<AvailableDelivery[]>([]);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [accepting, setAccepting] = useState<number | null>(null);
  const [approvalStatus, setApprovalStatus] = useState<'N'|'P'|'A'|'R'|null>(null);
  const [resubmeter, setResubmeter] = useState(false);
  const [rejectionReason, setRejectionReason] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    try {
      const statusRes = await driverService.getStatus();
      const status = statusRes.data.data.status;
      setApprovalStatus(status);
      setRejectionReason(statusRes.data.data.reason);

      if (status !== 'A') {
        setLoading(false);
        return;
      }

      const [statsRes, availRes, profileRes] = await Promise.all([
        driverService.getStats(),
        driverService.getAvailableDeliveries(),
        driverService.getProfile(),
      ]);
      setStats(statsRes.data.data);
      setAvailable(availRes.data.data);
      setOnline(!!profileRes.data.data.online);
    } catch (error) {
      console.error('Error loading driver data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 15000);
    return () => clearInterval(interval);
  }, [loadData]);

  const handleToggleOnline = async () => {
    setToggling(true);
    try {
      let lat: number | undefined;
      let lng: number | undefined;

      if (!online && navigator.geolocation) {
        const pos = await new Promise<GeolocationPosition>((resolve, reject) =>
          navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 })
        ).catch(() => null);
        if (pos) {
          lat = pos.coords.latitude;
          lng = pos.coords.longitude;
        }
      }

      await driverService.toggleOnline(!online, lat, lng);
      setOnline(!online);
    } catch (error) {
      console.error('Error toggling online:', error);
    } finally {
      setToggling(false);
    }
  };

  const handleAccept = async (deliveryId: number) => {
    setAccepting(deliveryId);
    try {
      await driverService.acceptDelivery(deliveryId);
      await loadData();
      if (stats?.activeDelivery || true) {
        navigate('/driver/delivery');
      }
    } catch (error: any) {
      alert(error.response?.data?.error || 'Erro ao aceitar entrega');
    } finally {
      setAccepting(null);
    }
  };

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('pt-CV', { style: 'currency', currency: 'CVE' }).format(value);

  const timeAgo = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'agora';
    if (mins < 60) return `${mins}min`;
    return `${Math.floor(mins / 60)}h ${mins % 60}min`;
  };

  if (loading) return <LoaderComponent />;

  // Sem inscrição (conta de estafeta sem ficha) ou a voltar a submeter depois de rejeitada
  if (approvalStatus === 'N' || (approvalStatus === 'R' && resubmeter)) {
    return <DriverRegister mode="apply" onApplied={() => { setResubmeter(false); setLoading(true); loadData(); }} />;
  }

  // Pending/Rejected states
  if (approvalStatus === 'P') {
    return (
      <div className="max-w-2xl mx-auto p-4">
        <Card className="p-8 text-center">
          <div className="w-20 h-20 mx-auto rounded-full bg-yellow-100 flex items-center justify-center mb-4">
            <FiClock className="text-4xl text-yellow-600" />
          </div>
          <h2 className="text-2xl font-bold mb-2">A aguardar aprovacao</h2>
          <p className="text-gray-600">
            O teu registo foi submetido e esta a ser revisto pela equipa Pagali. Vais receber um email quando a tua conta for aprovada.
          </p>
        </Card>
      </div>
    );
  }

  if (approvalStatus === 'R') {
    return (
      <div className="max-w-2xl mx-auto p-4">
        <Card className="p-8 text-center">
          <div className="w-20 h-20 mx-auto rounded-full bg-red-100 flex items-center justify-center mb-4">
            <FiX className="text-4xl text-red-600" />
          </div>
          <h2 className="text-2xl font-bold mb-2">Registo rejeitado</h2>
          {rejectionReason && (
            <p className="text-gray-600 mb-4">
              <strong>Motivo:</strong> {rejectionReason}
            </p>
          )}
          <button
            onClick={() => setResubmeter(true)}
            className="w-full py-3 bg-pagali-primary text-white rounded-xl font-medium mb-3"
          >
            Corrigir e submeter de novo
          </button>
          <p className="text-gray-500 text-sm">
            Contacta suporte@pagali.cv para mais informacoes.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-4 space-y-6">
      {/* Header com toggle online */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Pagali Driver</h1>
          <p className="text-sm text-gray-500">
            {online ? 'Estas online - recebes entregas' : 'Estas offline'}
          </p>
        </div>
        <button
          onClick={handleToggleOnline}
          disabled={toggling}
          className={`flex items-center gap-2 px-5 py-3 rounded-full font-semibold text-white transition-all ${
            online
              ? 'bg-green-500 hover:bg-green-600 shadow-lg shadow-green-200'
              : 'bg-gray-400 hover:bg-gray-500'
          }`}
        >
          <FiPower className="text-lg" />
          {toggling ? '...' : online ? 'Online' : 'Offline'}
        </button>
      </div>

      {/* Entrega ativa */}
      {stats?.activeDelivery && (
        <Card
          className="border-2 border-purple-500 bg-purple-50 cursor-pointer hover:bg-purple-100 transition-colors"
          onClick={() => navigate('/driver/delivery')}
        >
          <div className="p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-semibold text-purple-700">ENTREGA ATIVA</span>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${deliveryStatusConfig[stats.activeDelivery.status]?.color || ''}`}>
                {deliveryStatusConfig[stats.activeDelivery.status]?.label || stats.activeDelivery.status}
              </span>
            </div>
            <p className="font-semibold text-gray-900">{stats.activeDelivery.restaurantName}</p>
            <div className="flex items-center gap-1 text-sm text-gray-600 mt-1">
              <FiMapPin className="text-xs" />
              <span>{stats.activeDelivery.deliveryAddress}</span>
            </div>
            <div className="flex items-center justify-between mt-3">
              <span className="font-bold text-lg">{formatCurrency(stats.activeDelivery.orderTotal)}</span>
              <span className="flex items-center gap-1 text-purple-600 font-medium text-sm">
                Ver detalhes <FiChevronRight />
              </span>
            </div>
          </div>
        </Card>
      )}

      {/* Stats do dia */}
      <div className="grid grid-cols-2 gap-3">
        <Card className="p-4 text-center">
          <FiTruck className="mx-auto text-2xl text-pagali-primary mb-1" />
          <p className="text-2xl font-bold">{stats?.hoje?.totalHoje || 0}</p>
          <p className="text-xs text-gray-500">Entregas hoje</p>
        </Card>
        <Card className="p-4 text-center">
          <FiDollarSign className="mx-auto text-2xl text-green-500 mb-1" />
          <p className="text-2xl font-bold">{formatCurrency(stats?.hoje?.ganhosHoje || 0)}</p>
          <p className="text-xs text-gray-500">Ganhos hoje</p>
        </Card>
        <Card className="p-4 text-center">
          <FiStar className="mx-auto text-2xl text-yellow-500 mb-1" />
          <p className="text-2xl font-bold">{Number(stats?.rating || 0).toFixed(1)}</p>
          <p className="text-xs text-gray-500">Avaliacao</p>
        </Card>
        <Card className="p-4 text-center">
          <FiClock className="mx-auto text-2xl text-blue-500 mb-1" />
          <p className="text-2xl font-bold">{stats?.totalEntregas || 0}</p>
          <p className="text-xs text-gray-500">Total entregas</p>
        </Card>
      </div>

      {/* Ganhos semana/mes */}
      <div className="grid grid-cols-2 gap-3">
        <Card className="p-3">
          <p className="text-xs text-gray-500">Esta semana</p>
          <p className="text-lg font-bold">{formatCurrency(stats?.semana?.ganhosSemana || 0)}</p>
          <p className="text-xs text-gray-400">{stats?.semana?.totalSemana || 0} entregas</p>
        </Card>
        <Card className="p-3">
          <p className="text-xs text-gray-500">Este mes</p>
          <p className="text-lg font-bold">{formatCurrency(stats?.mes?.ganhosMes || 0)}</p>
          <p className="text-xs text-gray-400">{stats?.mes?.totalMes || 0} entregas</p>
        </Card>
      </div>

      {/* Entregas disponiveis */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold">Entregas disponiveis</h2>
          <button
            onClick={loadData}
            className="p-2 text-gray-500 hover:text-pagali-primary transition-colors"
          >
            <FiRefreshCw />
          </button>
        </div>

        {!online ? (
          <Card className="p-6 text-center">
            <FiPower className="mx-auto text-3xl text-gray-300 mb-2" />
            <p className="text-gray-500">Fica online para receber entregas</p>
          </Card>
        ) : available.length === 0 ? (
          <EmptyState
            icon={<FiTruck className="text-4xl" />}
            title="Sem entregas"
            description="Nao ha entregas disponiveis neste momento. Vamos notificar-te quando houver."
          />
        ) : (
          <div className="space-y-3">
            {available.map((delivery) => (
              <Card key={delivery.id} className="p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="font-semibold text-gray-900">{delivery.restaurantName}</p>
                    <p className="text-xs text-gray-400">
                      {delivery.orderCreatedAt && `Pedido ha ${timeAgo(delivery.orderCreatedAt)}`}
                    </p>
                  </div>
                  <span className="text-lg font-bold text-green-600">
                    {formatCurrency(delivery.deliveryFee || 0)}
                  </span>
                </div>

                <div className="flex items-start gap-3 my-3">
                  <div className="flex flex-col items-center">
                    <div className="w-2 h-2 bg-green-500 rounded-full" />
                    <div className="w-0.5 h-8 bg-gray-300" />
                    <div className="w-2 h-2 bg-red-500 rounded-full" />
                  </div>
                  <div className="flex-1 space-y-2">
                    <div>
                      <p className="text-xs text-gray-400">Recolher</p>
                      <p className="text-sm text-gray-700">{delivery.restaurantAddress}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">Entregar</p>
                      <p className="text-sm text-gray-700">{delivery.deliveryAddress}</p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    {delivery.distanceKm && (
                      <span className="flex items-center gap-1">
                        <FiNavigation className="text-xs" /> {delivery.distanceKm} km
                      </span>
                    )}
                    {delivery.estimatedMin && (
                      <span className="flex items-center gap-1">
                        <FiClock className="text-xs" /> ~{delivery.estimatedMin} min
                      </span>
                    )}
                    <span>Total: {formatCurrency(delivery.orderTotal)}</span>
                  </div>
                  <button
                    onClick={() => handleAccept(delivery.id)}
                    disabled={accepting === delivery.id || !!stats?.activeDelivery}
                    className="px-4 py-2 bg-pagali-primary text-white rounded-lg font-medium text-sm hover:bg-pagali-primary/90 disabled:opacity-50 transition-colors"
                  >
                    {accepting === delivery.id ? '...' : 'Aceitar'}
                  </button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DriverDashboard;
